import * as React from "react";
import { Trans, withTranslation, type WithTranslation } from "react-i18next";
import { Button } from "@thuthi/ui/components/button";
import { copyToClipboard } from "../app/utils";

const ExtLink = (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
  // eslint-disable-next-line jsx-a11y/anchor-has-content
  <a {...props} target="_blank" rel="noopener noreferrer" className="text-primary underline-offset-4 hover:underline" />
);

interface ErrorBoundaryImplProps extends WithTranslation {
  children?: React.ReactNode;
}

interface ErrorBoundaryImplState {
  error: boolean;
  stack: string | null;
  unsupportedIndexedDB: boolean;
}

class ErrorBoundaryImpl extends React.Component<ErrorBoundaryImplProps, ErrorBoundaryImplState> {
  constructor(props: ErrorBoundaryImplProps) {
    super(props);
    this.state = {
      error: false,
      stack: null,
      unsupportedIndexedDB: false,
    };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary] Error caught", error, info);

    // Special case for unsupported IndexedDB in Private Browsing mode (Firefox, Safari), see
    // - https://github.com/dexie/Dexie.js/issues/312
    // - https://bugzilla.mozilla.org/show_bug.cgi?id=781982
    const isUnsupportedIndexedDB =
      error?.name === "InvalidStateError" || (error?.name === "DatabaseClosedError" && error?.message?.indexOf("InvalidStateError") !== -1);

    if (isUnsupportedIndexedDB) {
      this.handleUnsupportedIndexedDB();
    } else {
      this.handleError(error, info);
    }
  }

  handleError(error: Error, info: React.ErrorInfo) {
    const componentStack = (info.componentStack ?? "")
      .trim()
      .split("\n")
      .map((line) => `  at ${line}`)
      .join("\n");
    const parts = [error.toString()];
    if (error.stack) parts.push(error.stack);
    parts.push(componentStack);
    this.setState({
      error: true,
      stack: parts.join("\n"),
    });
  }

  handleUnsupportedIndexedDB() {
    this.setState({
      error: true,
      unsupportedIndexedDB: true,
    });
  }

  copyStack() {
    copyToClipboard(`${this.state.stack}\n`);
  }

  renderUnsupportedIndexedDB() {
    const { t } = this.props;
    return (
      <div className="m-5">
        <h2 className="text-lg font-semibold">{t("error_boundary_unsupported_indexeddb_title")} 😮</h2>
        <p className="max-w-[600px]">
          <Trans
            i18nKey="error_boundary_unsupported_indexeddb_description"
            components={{
              githubLink: <ExtLink href="https://github.com/binwiederhier/ntfy/issues/208" />,
              discordLink: <ExtLink href="https://discord.gg/cT7ECsZj9w" />,
              matrixLink: <ExtLink href="https://matrix.to/#/#ntfy:matrix.org" />,
            }}
          />
        </p>
      </div>
    );
  }

  renderError() {
    const { t } = this.props;
    return (
      <div className="m-5">
        <h2 className="text-lg font-semibold">{t("error_boundary_title")} 😮</h2>
        <p>
          <Trans
            i18nKey="error_boundary_description"
            components={{
              githubLink: <ExtLink href="https://github.com/binwiederhier/ntfy/issues" />,
              discordLink: <ExtLink href="https://discord.gg/cT7ECsZj9w" />,
              matrixLink: <ExtLink href="https://matrix.to/#/#ntfy:matrix.org" />,
            }}
          />
        </p>
        <div className="flex gap-1.5">
          <Button variant="outline" onClick={() => this.copyStack()}>
            {t("error_boundary_button_copy_stack_trace")}
          </Button>
          <Button variant="outline" onClick={() => window.location.reload()}>
            {t("error_boundary_button_reload_ntfy")}
          </Button>
        </div>
        <h3 className="mt-4 font-semibold">{t("error_boundary_stack_trace")}</h3>
        <pre className="overflow-x-auto text-xs">{this.state.stack}</pre>
      </div>
    );
  }

  render() {
    if (this.state.error) {
      if (this.state.unsupportedIndexedDB) {
        return this.renderUnsupportedIndexedDB();
      }
      return this.renderError();
    }
    return this.props.children;
  }
}

const ErrorBoundary = withTranslation()(ErrorBoundaryImpl); // Adds props.t
export default ErrorBoundary;
